/*
  NISS-0138 — Legal & Documentation Referral Triggers helper.

  Pure data transform. Inputs: provider intake responses (answers.ld_*).
  Outputs: professionally-synthesised matrix rows for the client-facing
  report and the plain-text / markdown exports.

  Deliberate separation:
   * Provider response  -> answers.ld_*
   * Consultant note    -> answers[id].__evidence_note / consultantNotes (never read here)
   * Customer comment   -> not surfaced to the client report
   * Referral trigger   -> synthesised label below
   * Report finding     -> synthesised label below

  Phase 8C — Evidence status vocabulary normalised to the six canonical
  evidence states from `questionnaire.js` (see CANONICAL_EVIDENCE_STATE).
  Non-canonical legacy labels ("Evidence provided", "Evidence not yet
  provided", "Not assessed due to scope boundary", "Needs clarification")
  have been mapped onto the canonical vocabulary. The redFlag and
  referralTrigger columns remain unchanged — they are not evidence states,
  they are workflow signals.

  This module NEVER reads raw comment fields, consultant notes, or customer
  free-text remarks. It only transforms provider yes/no/unsure/na responses
  into professional-language readiness findings.
*/

import { CANONICAL_EVIDENCE_STATE } from "@/lib/questionnaire";

// Provider yes/no/unsure/na answers on referral-trigger questions carry
// evidence-status semantics on the "Evidence status" column of the report
// matrix. Canonical mapping:
//   yes    -> Provider-reported   (provider says the item is in place; not
//                                   yet independently sighted by consultant)
//   no     -> Source required     (evidence is not in place — needs to be
//                                   sourced or produced)
//   na     -> Not applicable, with explanation
//   unsure -> Requires clarification
function statusFromAnswer(value) {
  if (value === "yes") return CANONICAL_EVIDENCE_STATE.PROVIDER_REPORTED;
  if (value === "no") return CANONICAL_EVIDENCE_STATE.SOURCE_REQUIRED;
  if (value === "na") return CANONICAL_EVIDENCE_STATE.NOT_APPLICABLE;
  if (value === "unsure") return CANONICAL_EVIDENCE_STATE.REQUIRES_CLARIFICATION;
  return CANONICAL_EVIDENCE_STATE.REQUIRES_CLARIFICATION;
}

// Red-flag column is a synthesised workflow signal (not an evidence state).
// Wording preserved from Pass 6B other than the "Not assessed due to scope
// boundary" case, which is aligned to canonical vocabulary.
function concern(value, positive = "No red flag recorded") {
  if (value === "yes") return "Referral trigger present";
  if (value === "no") return positive;
  if (value === "na") return CANONICAL_EVIDENCE_STATE.NOT_APPLICABLE;
  return CANONICAL_EVIDENCE_STATE.REQUIRES_CLARIFICATION;
}

// Referral-trigger column — workflow signal only. Non-yes/no answers fall
// through to canonical clarification wording.
function trigger(present, presentLabel) {
  if (present) return presentLabel;
  return CANONICAL_EVIDENCE_STATE.REQUIRES_CLARIFICATION;
}

export function buildLegalReferralRows(answers = {}) {
  return [
    {
      area: "Registration pathway and SIL / 0138 exposure",
      evidenceRequested: "NDIS registration certificate, registration scope, service model summary",
      evidenceStatus: statusFromAnswer(answers.ld_registration_status_confirmed),
      redFlag: concern(answers.ld_sil_0138_uncertainty),
      referralTrigger: trigger(
        answers.ld_sil_0138_uncertainty === "yes" || answers.ld_sil_0138_uncertainty === "unsure",
        "Audit / registration review trigger"
      ),
      suggestedNextAction:
        "Confirm registration pathway, scope and SIL / 0138 exposure before relying on the readiness snapshot."
    },
    {
      area: "Participant service agreements",
      evidenceRequested: "Current participant service agreement template",
      evidenceStatus: statusFromAnswer(answers.ld_service_agreements_current),
      redFlag: concern(answers.ld_service_agreements_current, "No immediate red flag recorded"),
      referralTrigger: trigger(
        answers.ld_service_agreements_current === "no" || answers.ld_service_agreements_current === "unsure",
        "Legal referral trigger"
      ),
      suggestedNextAction:
        "Prepare the service agreement template for independent professional review if missing, unclear or out of date."
    },
    {
      area: "Privacy and consent",
      evidenceRequested: "Privacy policy, consent form and breach-response pathway",
      evidenceStatus: statusFromAnswer(answers.ld_privacy_consent_current),
      redFlag: concern(answers.ld_privacy_consent_current, "No immediate red flag recorded"),
      referralTrigger: trigger(
        answers.ld_privacy_consent_current === "no" || answers.ld_privacy_consent_current === "unsure",
        "Privacy review trigger"
      ),
      suggestedNextAction:
        "Check privacy, consent and information-handling materials before collecting sensitive information."
    },
    {
      area: "Complaints and incident systems",
      evidenceRequested: "Complaints procedure, incident management procedure and escalation records",
      evidenceStatus: statusFromAnswer(answers.ld_complaints_incidents_documented),
      redFlag: concern(answers.ld_current_dispute_or_concern),
      referralTrigger: trigger(
        answers.ld_current_dispute_or_concern === "yes",
        "Safeguarding escalation trigger"
      ),
      suggestedNextAction:
        "Escalate active disputes, incidents, privacy concerns or unresolved complaints through the appropriate professional pathway."
    },
    {
      area: "Worker and contractor controls",
      evidenceRequested: "Worker screening register, training matrix, supervision evidence and contractor template",
      evidenceStatus: statusFromAnswer(answers.ld_worker_screening_training_supervision),
      redFlag:
        answers.ld_contractors_labour_hire === "yes"
          ? "Contractor or labour-hire model reported"
          : concern(answers.ld_worker_screening_training_supervision),
      referralTrigger: trigger(
        answers.ld_contractors_labour_hire === "yes",
        "Legal referral trigger"
      ),
      suggestedNextAction:
        "Review workforce, contractor and labour-hire controls before expanding service delivery."
    },
    {
      area: "Agreements about tenancy, housing and support arrangements",
      evidenceRequested: "Agreements about tenancy, housing and support arrangements — supporting documents, if relevant",
      evidenceStatus: statusFromAnswer(answers.ld_tenancy_support_overlap),
      redFlag:
        answers.ld_tenancy_support_overlap === "yes"
          ? "Housing and support overlap reported"
          : concern(answers.ld_tenancy_support_overlap),
      referralTrigger: trigger(
        answers.ld_tenancy_support_overlap === "yes" || answers.ld_tenancy_support_overlap === "unsure",
        "Legal referral trigger"
      ),
      suggestedNextAction:
        "Clarify the separation between housing, tenancy and support obligations where they overlap."
    },
    {
      area: "Website and marketing claims",
      evidenceRequested: "Website, brochure or proposal copy",
      evidenceStatus: statusFromAnswer(answers.ld_marketing_claims_checked),
      redFlag: concern(answers.ld_marketing_claims_checked, "No immediate red flag recorded"),
      referralTrigger: trigger(
        answers.ld_marketing_claims_checked === "no" || answers.ld_marketing_claims_checked === "unsure",
        "Legal referral trigger"
      ),
      suggestedNextAction:
        "Check public claims for overstatement before using them in provider-facing material."
    }
  ];
}
